import Flickity from 'flickity';

import flickitySmoothScrolling from '../globals/flickity-smooth-scrolling';
import ProductVideo from '../media/product-video';

import {ProductAddForm} from './product-form';
import {makeSwatches} from './swatch';
import {Popout} from './popout';

const selectors = {
  addToCart: '[data-add-to-cart]',
  deferredMedia: '[data-deferred-media]',
  deferredMediaButton: '[data-deferred-media-button]',
  popupClose: '[data-popup-close]',
  popout: '[data-popout]',
  product: '[data-product]',
  productForm: '[data-product-form]',
  productMediaSlider: '[data-product-single-media-slider]',
  productMediaWrapper: '[data-product-single-media-wrapper]',
  productModel: '[data-model]',
  productJSON: '[data-product-json]',
  quickViewFootInner: '[data-quick-view-foot-inner]',
};

const classes = {
  hasMediaActive: 'has-media-active',
  isLoading: 'is-loading',
  mediaHidden: 'media--hidden',
  noOutline: 'no-outline',
  quickViewVisible: 'js-quick-view-visible',
  notificationPopupVisible: 'notification-popup-visible',
  fill: 'fill',
  quickViewAnimateIn: 'popup-quick-view--animate-in',
  quickViewAnimateOut: 'popup-quick-view--animate-out',
  quickViewAnimated: 'popup-quick-view--animated',
  quickView: 'popup-quick-view',
  quickViewFromCart: 'js-quick-view-from-cart',
};

const attributes = {
  id: 'id',
  mediaId: 'data-media-id',
  sectionId: 'data-section-id',
  handle: 'data-handle',
  loaded: 'loaded',
  tabindex: 'tabindex',
};

const ids = {
  addToCartFormId: 'AddToCartForm--',
  addToCartId: 'AddToCart--',
};

class LoadQuickview {
  constructor(popup, pswpElement) {
    this.popup = popup;
    this.pswpElement = pswpElement;
    this.quickViewFoot = this.pswpElement.querySelector(selectors.quickViewFootInner);
    this.container = this.pswpElement.querySelector(selectors.product);
    this.popupClose = this.container.querySelector(selectors.popupClose);
    this.sectionId = this.container.getAttribute(attributes.sectionId);
    this.slider = this.container.querySelector(selectors.productMediaSlider);
    this.mediaItems = this.container.querySelectorAll(selectors.productMediaWrapper);
    this.flkty = null;
    this.product = JSON.parse(this.container.querySelector(selectors.productJSON).innerHTML);
    this.body = document.body;

    this.prevent3dModelSubmitEvent = (e) => this.prevent3dModelSubmit(e);
    this.closeQuickviewOnMobileEvent = () => this.closeQuickviewOnMobile();
    this.closeOnAnimationEndEvent = (e) => this.closeOnAnimationEnd(e);
    this.closeOnEscapeEvent = (e) => this.closeOnEscape(e);
    this.outerCloseEvent = (e) => {
      if (!this.container.contains(e.target)) {
        this.closePopup(e);
      }
    };

    this.init();
  }

  init() {
    this.addFormSuffix();
    this.initProductSlider();
    this.initProductVideo();
    this.initProductModel();
    this.initShopifyXrLaunch();

    // Init swatches
    makeSwatches(this.container);

    // Init popouts
    const popoutElements = this.container.querySelectorAll(selectors.popout);
    let popouts = {};

    if (popoutElements.length) {
      popoutElements.forEach((popout, index) => {
        popouts[index] = new Popout(popout);
      });
    }

    new ProductAddForm(this.container.parentNode);

    if (Shopify.PaymentButton) {
      Shopify.PaymentButton.init();
    }

    this.container.classList.remove(classes.isLoading);

    // Close the Quickview on Mobile when resizing
    document.addEventListener('theme:resize:width', this.closeQuickviewOnMobileEvent);

    // Prevent 3d models button redirecting to cart page when enabling/disabling the model a couple of times
    document.addEventListener('submit', this.prevent3dModelSubmitEvent);

    // Custom closing events
    this.popupClose.addEventListener('keyup', (e) => {
      if (e.keyCode === theme.keyboardKeys.ENTER || e.keyCode === theme.keyboardKeys.SPACE) {
        this.closePopup(e);
      }
    });

    this.popupClose.addEventListener('click', (e) => {
      this.closePopup(e);
    });

    this.pswpElement.addEventListener('click', this.outerCloseEvent);

    document.dispatchEvent(new CustomEvent('theme:popup:open', {bubbles: true}));

    this.popup.listen('preventDragEvent', (e, isDown, preventObj) => {
      preventObj.prevent = false;
    });

    this.pswpElement.addEventListener('mousedown', () => {
      this.popup.framework.unbind(window, 'pointermove pointerup pointercancel', this.popup);
    });

    // Opening event
    this.popup.listen('initialZoomInEnd', () => {
      this.body.classList.add(classes.quickViewVisible);

      slate.a11y.trapFocus({
        container: this.container,
      });
    });

    this.pswpElement.addEventListener('animationend', this.closeOnAnimationEndEvent);

    this.popup.listen('destroy', () => {
      if (this.flkty) {
        this.flkty.pausePlayer();
      }
      this.body.classList.remove(classes.quickViewVisible);
      document.removeEventListener('keyup', this.closeOnEscapeEvent);
      document.addEventListener('keyup', this.closeOnEscapeEvent);
      this.pswpElement.removeEventListener('click', this.outerCloseEvent);
      this.pswpElement.removeEventListener('animationend', this.closeOnAnimationEndEvent);
      document.removeEventListener('theme:resize:width', this.closeQuickviewOnMobileEvent);
      document.removeEventListener('submit', this.prevent3dModelSubmitEvent);
    });

    document.addEventListener('keyup', this.closeOnEscapeEvent);
    document.addEventListener('theme:cart:added', () => {
      if (this.pswpElement.classList.contains(classes.quickView)) {
        this.pswpElement.classList.add(classes.quickViewAnimateOut);
      }
    });

    this.animateInQuickview();
  }

  // Prevents the 3d model buttons submitting the form
  prevent3dModelSubmit(event) {
    if (event.submitter.closest(selectors.deferredMedia) && event.submitter.closest(selectors.productForm)) {
      event.preventDefault();
    }
  }

  closeQuickviewOnMobile() {
    if (window.innerWidth < window.theme.sizes.large && this.body.classList.contains(classes.quickViewVisible)) {
      this.popup.close();
    }
  }

  animateInQuickview() {
    this.pswpElement.classList.add(classes.quickViewAnimateIn);

    this.quickViewFoot.addEventListener('animationend', (event) => {
      if (event.animationName == 'quickViewFootAnimateIn') {
        this.pswpElement.classList.add(classes.quickViewAnimated);
        this.pswpElement.classList.remove(classes.quickViewAnimateIn);
        document.body.classList.remove(classes.quickViewFromCart); // Clear the class that we are adding in quick-view-popup.js when the animation ends
      }
    });
  }

  closePopup(event) {
    event.preventDefault();
    this.pswpElement.classList.add(classes.quickViewAnimateOut); // Adding this class triggers the 'animationend' event which calls closeOnAnimationEndEvent()
  }

  closeOnAnimationEnd(event) {
    if (event.animationName == 'quickViewAnimateOut') {
      this.popup.template.classList.remove(classes.quickViewAnimateOut, classes.quickViewAnimated);
      this.popup.close();
    }
  }

  closeOnEscape(e) {
    const isQuickViewVisible = this.body.classList.contains(classes.quickViewVisible);
    const isNotificationVisible = this.body.classList.contains(classes.notificationPopupVisible);

    if (e.keyCode === theme.keyboardKeys.ESCAPE && isQuickViewVisible && !isNotificationVisible) {
      this.closePopup(e);
    }
  }

  initProductSlider() {
    if (this.mediaItems.length > 1) {
      this.flkty = new Flickity(this.slider, {
        wrapAround: true,
        cellAlign: 'left',
        pageDots: false,
        prevNextButtons: true,
        adaptiveHeight: false,
        pauseAutoPlayOnHover: false,
        selectedAttraction: 0.2,
        friction: 1,
        autoPlay: false,
        on: {
          ready: () => {
            this.slider.setAttribute(attributes.tabindex, '-1');

            // This resize should happen when the show animation of the PhotoSwipe starts and after PhotoSwipe adds the custom 'popup--quickview' class with the mainClass option. This class is changing the slider width with CSS and looks like this is happening after the slider loads which is breaking it. That's why we need to call the resize() method here.
            requestAnimationFrame(() => {
              this.flkty.resize();
            });
          },
          settle: () => {
            const currentSlide = this.flkty.selectedElement;
            const mediaId = currentSlide.getAttribute(attributes.mediaId);
            currentSlide.setAttribute(attributes.tabindex, '0');

            this.flkty.cells.forEach((slide) => {
              if (slide.element === currentSlide) {
                return;
              }

              slide.element.setAttribute(attributes.tabindex, '-1');
            });
            this.switchMedia(mediaId);
          },
        },
      });

      // Toggle flickity draggable functionality based on media play/pause state
      if (this.mediaItems.length) {
        this.mediaItems.forEach((el) => {
          el.addEventListener('theme:media:play', () => {
            this.flkty.options.draggable = false;
            this.flkty.updateDraggable();
            el.closest(selectors.productMediaSlider).classList.add(classes.hasMediaActive);
          });

          el.addEventListener('theme:media:pause', () => {
            this.flkty.options.draggable = true;
            this.flkty.updateDraggable();
            el.closest(selectors.productMediaSlider).classList.remove(classes.hasMediaActive);
          });
        });
      }

      // iOS smooth scrolling fix
      flickitySmoothScrolling(this.slider);
    }
  }

  switchMedia(mediaId) {
    const selectedMedia = this.container.querySelector(`${selectors.productMediaWrapper}[${attributes.mediaId}="${mediaId}"]`);
    const isFocusEnabled = !this.body.classList.contains(classes.noOutline);

    // Pause other media
    if (this.mediaItems.length) {
      this.mediaItems.forEach((media) => {
        media.dispatchEvent(new CustomEvent('theme:media:hidden'), {bubbles: true});
        media.classList.add(classes.mediaHidden);
      });
    }

    if (isFocusEnabled) {
      selectedMedia.focus();
    }

    selectedMedia.closest(selectors.productMediaSlider).classList.remove(classes.hasMediaActive);
    selectedMedia.classList.remove(classes.mediaHidden);
    selectedMedia.dispatchEvent(new CustomEvent('theme:media:visible'), {bubbles: true});

    // If media is not loaded, trigger poster button click to load it
    const deferredMedia = selectedMedia.querySelector(selectors.deferredMedia);
    if (deferredMedia && deferredMedia.getAttribute(attributes.loaded) !== 'true') {
      selectedMedia.querySelector(selectors.deferredMediaButton).dispatchEvent(new Event('click'));
    }
  }

  initProductVideo() {
    this.videos = new ProductVideo(this.container);
  }

  initProductModel() {
    const modelItems = this.container.querySelectorAll(selectors.productModel);
    if (modelItems.length) {
      modelItems.forEach((element) => {
        PaloAlto.ProductModel.init(element, this.sectionId);
      });
    }
  }

  initShopifyXrLaunch() {
    document.addEventListener('shopify_xr_launch', () => {
      const currentMedia = this.container.querySelector(`${selectors.productModel}:not(.${classes.mediaHidden})`);
      currentMedia.dispatchEvent(new CustomEvent('xrLaunch'));
    });
  }

  addFormSuffix() {
    const formSuffix = `${this.sectionId}-${this.product.handle}`;
    const productForm = this.container.querySelector(selectors.productForm);
    const addToCart = this.container.querySelector(selectors.addToCart);

    productForm.setAttribute(attributes.id, ids.addToCartFormId + formSuffix);
    addToCart.setAttribute(attributes.id, ids.addToCartId + formSuffix);
  }
}

export {LoadQuickview};
